import React, { useState, useEffect, useRef } from 'react'
import type { Race } from 'src/types/domain/Race'
import type { Marble } from 'src/types/domain/Marble'
import FlagCheckeredIcon from 'src/app/assets/icons/ico-flag-checkered.svg'
import {
  getLastWinners,
  getSortedRaceResults,
  isRaceSettled,
  getLatestEvent
} from 'src/utils/raceUtils'
import { useClientSideTranslation } from 'src/services/i18n'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import { useGetCurrentLanguage } from 'src/store/game'
import {
  CrashRoundModal,
  deriveCrashPastResults,
  formatTimeAgo,
  getMultiplierTier,
  hasRoundFairness,
  resolveModalRace
} from '../CrashMultiplierHistory'
import { BirdiePastRounds } from '../BirdieBoard/BirdiePastRounds'
import { Button } from '../Button'
import { MarbleItem } from '../MarbleItem'
import { pastResultsVariant } from './pastResults'
import { useVisualCrashStatus } from '../CrashChipPanel/useVisualCrash'
import { resolveCrashEventInfo } from '../CrashChipPanel/crashEventInfo'

interface TopThree {
  first?: Marble
  second?: Marble
  third?: Marble
}

type Props = Readonly<{
  resultsToSubmit: number
  races: Race[]
  marbles: Marble[]
  gameType: string
  openRightSidebar: (tab: 'betHistory' | 'pastRaces' | 'stats') => void
}>

export function ShowRaceResults({
  resultsToSubmit,
  races,
  marbles,
  gameType,
  openRightSidebar
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const [isActive, setIsActive] = useState(false)
  // Crash entries open the round's provably-fair modal (Aviator pattern) —
  // a tapped pill selects a round id; the modal renders only while that id
  // still resolves against the store (rounds get pruned as history rolls).
  const [selectedRaceId, setSelectedRaceId] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const NUM_OF_WINNERS = 8

  // Crash games surface their past rounds HERE (v8) — same right-side
  // placement and expand feel as every other game — as heat-tinted
  // multiplier pills instead of marble winners.
  const variant = pastResultsVariant(gameType)
  const isCrashGame = variant === 'crash'
  // …and B19b brings Birdie in beside them, as the round discs. Owner: "if it's
  // this golf birdie game it will show like the design you've made else like
  // it's shown for the other games so we don't reinvent it all". Everything
  // around the results — the container, the "Past Games" button, the expand
  // feel, the phone row — is the shared one; only what fills the slot differs.
  // The discs, their reveal clock and their modal live in `<BirdiePastRounds>`
  // so this component gains no hook and no other game's render path moves.
  const isBirdieGame = variant === 'birdie'

  // Determine how many places to show (1, 2, or 3)
  const placesToShow = Math.min(resultsToSubmit, 3)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsActive(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const endedRaces = Array.isArray(races) ? races.filter(isRaceSettled) : []
  const winners = getLastWinners(endedRaces, marbles, NUM_OF_WINNERS)
  // v39 — ANTI-SPOILER: the newest round settles on the EVENT clock, which
  // leads the viewer's frames — its pill (wearing the crash multiplier!)
  // used to appear in this row ~a second before the bust was visible on
  // screen, the exact tell class every other crash surface gates. While
  // the latest round's crash is known-but-unseen ('armed'), hold its pill
  // out; it files in the moment the crash lands on the frames — which,
  // with the entrance animation, IS the payoff. 'undriven' (no visual
  // driver) falls back to the event clock like every gated surface.
  const visualCrashStatus = useVisualCrashStatus()
  const latestCrashRace = isCrashGame ? races[0] : undefined
  const holdNewestId =
    latestCrashRace &&
    visualCrashStatus === 'armed' &&
    resolveCrashEventInfo(latestCrashRace.raceEvents).crashKnown
      ? latestCrashRace.uuid
      : null
  const crashResults = isCrashGame
    ? deriveCrashPastResults(races, NUM_OF_WINNERS + 1)
        .filter((r) => r.raceId !== holdNewestId)
        .slice(0, NUM_OF_WINNERS)
    : []
  // Birdie asks the cheap question here — "has anything settled?" — and lets
  // the branch itself decide what is showable: whether a settled round has a
  // committed board to price it, and whether its reveal hold has elapsed, are
  // `<BirdiePastRounds>`' own contract and neither is knowable without its
  // hooks.
  //
  // So the two answers can differ, and the DELIBERATE result is a rail holding
  // only the "Past Games" button — during each round's reveal hold, and for as
  // long as a game's settled rounds carry no committed odds to price. Kept that
  // way on purpose: that button is the entry to the history sidebar, which does
  // its own deep read of 50 rounds and has rows to show when this rail has none,
  // so dropping it would take away the way in rather than tidy anything up.
  // (The alternative — asking `birdiePastRounds` here — has to read the clock
  // during render, which this board refuses to do anywhere, and the reveal-hold
  // timer lives inside the child that this gate would be keeping unmounted.)
  const hasSharedResults = isCrashGame ? crashResults.length > 0 : winners.length > 0
  const hasResults = isBirdieGame ? endedRaces.length > 0 : hasSharedResults

  // Get the latest race events
  const latestRace = races[0]
  const latestEvent = latestRace ? getLatestEvent(latestRace.raceEvents) : null
  const isRaceStarted = latestEvent?.eventType === 'race_started'

  // Un-gated (was crash-only, which left the counting games' modal dead): the
  // id can only have been set by a result the round-fairness check opened up.
  const modalRace = resolveModalRace(races, selectedRaceId)

  // Which of the RENDERED past rounds carry a provable-fairness story to open.
  // Keyed by round id so the marble-winner tiles below can gate their own click
  // target on the ROUND, never on the game family. Scoped to the tiles actually
  // shown — this recomputes on every store tick.
  const shownWinners = winners.slice(0, NUM_OF_WINNERS)
  const fairnessRaceIds = new Set(
    endedRaces
      .filter((race) => shownWinners.some((winner) => winner.raceId === race.uuid))
      .filter(hasRoundFairness)
      .map((race) => race.uuid)
  )

  const openPastRacesTab = () => {
    setIsActive(false)
    openRightSidebar('pastRaces')
  }

  const handleContainerClick = (e: React.MouseEvent) => {
    // Don't set active if clicking the button or its children
    if (!e.target || (e.target as Element).closest('.past-races-button')) {
      return
    }
    setIsActive(true)
  }

  const getTopThree = (raceId: string): TopThree => {
    const race = endedRaces.find((r) => r.uuid === raceId)
    if (!race) return {}

    const sortedResults = getSortedRaceResults(race, marbles)
    if (!sortedResults || sortedResults.length === 0) return {}

    return {
      first: sortedResults[0]?.marble,
      second: sortedResults[1]?.marble,
      third: sortedResults[2]?.marble
    }
  }

  // Render position labels based on placesToShow
  const renderPositionLabels = () => {
    const labels = []

    if (placesToShow >= 1) {
      labels.push(
        <div className="green" key="1st">
          {t('1st')}
        </div>
      )
    }

    if (placesToShow >= 2) {
      labels.push(
        <div className="yellow" key="2nd">
          {t('2nd')}
        </div>
      )
    }

    if (placesToShow >= 3) {
      labels.push(
        <div className="blue" key="3rd">
          {t('3rd')}
        </div>
      )
    }

    return labels
  }

  // Crash entries: heat-tinted multiplier pills — tapping one opens the v6
  // provably-fair round modal (same flow the old top strip carried).
  const renderCrashResults = () => (
    <div className="crash-results">
      {crashResults.map(({ raceId, multiplier, endedAt }) => (
        <button
          type="button"
          className={`crash-multiplier-pill ${getMultiplierTier(multiplier)}`}
          key={raceId}
          title={endedAt ? formatTimeAgo(endedAt, t) : undefined}
          aria-label={`${t('Round details')}: ${multiplier.toFixed(2)}x`}
          onClick={() => setSelectedRaceId(raceId)}
        >
          <span>{multiplier.toFixed(2)}x</span>
        </button>
      ))}
    </div>
  )

  // Render marble winners (default for non-crash games). A tile whose round
  // published a commitment or a reveal — today the counting (CCTV) games —
  // opens the same provably-fair modal the crash pills open. Rounds with no
  // fairness artifacts stay plain, non-interactive tiles.
  const renderMarbleResults = () => (
    <div className="marbles">
      {shownWinners.map(({ raceId, marble }) => (
        <div
          className={`marble-wrapper ${fairnessRaceIds.has(raceId) ? 'marble-wrapper--fairness' : ''}`}
          key={raceId}
          {...(fairnessRaceIds.has(raceId)
            ? {
                role: 'button' as const,
                tabIndex: 0,
                'aria-label': t('Round details'),
                onClick: () => setSelectedRaceId(raceId),
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedRaceId(raceId)
                  }
                }
              }
            : {})}
        >
          <MarbleItem
            className={`marble-${marble.color}`}
            marble={marble}
            tooltip={getTranslatedMarbleColor(marble.color, t)}
          />
          {placesToShow > 1 && (
            <div className="additional-winners">
              {(() => {
                const topThree = getTopThree(raceId)
                return (
                  <>
                    {placesToShow >= 2 && topThree.second && (
                      <MarbleItem
                        marble={topThree.second}
                        tooltip={getTranslatedMarbleColor(topThree.second.color, t)}
                        className={`marble-${topThree.second.color}`}
                      />
                    )}

                    {placesToShow >= 3 && topThree.third && (
                      <MarbleItem
                        marble={topThree.third}
                        tooltip={getTranslatedMarbleColor(topThree.third.color, t)}
                        className={`marble-${topThree.third.color}`}
                      />
                    )}
                  </>
                )
              })()}
            </div>
          )}
        </div>
      ))}
    </div>
  )

  return (
    hasResults && (
      <div className="show-past-winners-outer">
        <div
          ref={containerRef}
          className={`show-past-winners ${isCrashGame ? 'crash-game' : ''} ${isBirdieGame ? 'birdie-game' : ''} ${resultsToSubmit === 1 ? 'single-result' : ''} ${isActive ? 'active' : ''} ${
            isRaceStarted ? 'race-started' : ''
          }`}
          onClick={handleContainerClick}
        >
          <Button className="past-races-button" onClick={openPastRacesTab}>
            <FlagCheckeredIcon />
            <span>{t('Past Games')}</span>
          </Button>
          {isBirdieGame ? (
            <BirdiePastRounds marbles={marbles} races={races} />
          ) : isCrashGame ? (
            renderCrashResults()
          ) : (
            <>
              <div className="marble-label-container">{renderPositionLabels()}</div>
              {renderMarbleResults()}
            </>
          )}
        </div>

        {modalRace && (
          <CrashRoundModal race={modalRace} onClose={() => setSelectedRaceId(null)} />
        )}
      </div>
    )
  )
}
